import { FieldConfig } from '../types/visual-editor';

export class JsonAnalyzer {
  static analyzeJson(data: any, parentKey: string = ''): FieldConfig[] {
    if (!data || typeof data !== 'object') {
      return [];
    }

    return Object.entries(data).map(([key, value]) => {
      const fieldConfig: FieldConfig = {
        key,
        label: this.generateLabel(key),
        type: this.detectType(value, key),
        value,
        required: false,
        description: this.generateDescription(key, value),
      };

      if (Array.isArray(value)) {
        fieldConfig.arrayItemType = this.detectArrayItemType(value);
        if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
          // Crear una estructura híbrida que incluya todos los campos de todos los elementos
          const hybridStructure = this.createHybridArrayItemStructure(value);
          fieldConfig.arrayItemConfig = {
            key: 'item',
            label: 'Item',
            type: 'object',
            value: value[0], // Usar el primer elemento real del array
            children: this.analyzeJson(hybridStructure, `${parentKey}.${key}[0]`),
          };
        }
      } else if (typeof value === 'object' && value !== null) {
        fieldConfig.children = this.analyzeJson(value, `${parentKey}.${key}`);
      }

      return fieldConfig;
    });
  }

  private static detectType(value: any, key: string = ''): FieldConfig['type'] {
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object' && value !== null) return 'object';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    
    // Detectar campos de imagen por nombre
    if (this.isImageField(key)) return 'image';
    
    return 'string';
  }

  private static isImageField(key: string): boolean {
    // Detectar campos que son para "Images" 
    if (key.toLowerCase().includes('image')) {
      return true;
    }
    if (key.toLowerCase().includes('logo')) {
      return true;
    }
    return false;
  }

  private static detectArrayItemType(array: any[]): FieldConfig['arrayItemType'] {
    if (array.length === 0) return 'string';
    return this.detectType(array[0], '') as FieldConfig['arrayItemType'];
  }

  private static generateLabel(key: string): string {
    // Convertir camelCase, snake_case, kebab-case a formato legible
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]/g, ' ')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private static generateDescription(key: string, value: any): string {
    const type = this.detectType(value, key);
    const typeDescriptions: Record<string, string> = {
      string: 'Texto',
      number: 'Número',
      boolean: 'Verdadero/Falso',
      array: `Lista de ${Array.isArray(value) && value.length > 0 ? this.detectType(value[0], '') : 'elementos'}`,
      object: 'Objeto con propiedades',
      image: 'Imagen',
    };
    return typeDescriptions[type] || 'Campo';
  }

  /**
   * Crea una estructura híbrida que combina todos los campos de todos los elementos del array
   * Esto permite que el editor visual maneje arrays con elementos de diferentes estructuras
   * Usa valores del primer elemento cuando están disponibles para preservar datos reales
   */
  private static createHybridArrayItemStructure(array: any[]): any {
    if (array.length === 0) return {};
    
    const hybridStructure: any = {};
    const allKeys = new Set<string>();
    const firstElement = array[0];
    
    // Recopilar todas las claves de todos los elementos
    array.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });
    
    // Para cada clave, procesar según su tipo
    allKeys.forEach(key => {
      // Recopilar todos los valores de esta clave en todos los elementos
      const values = array
        .map(item => item[key])
        .filter(value => value !== undefined);
      
      if (values.length === 0) return;
      
      // Determinar el tipo más común
      const types = values.map(value => this.detectType(value, key));
      const mostCommonType = this.getMostCommonType(types);
      
      // Si la clave existe en el primer elemento, usar su valor (o procesarlo recursivamente)
      const firstElementHasKey = firstElement && firstElement[key] !== undefined;
      
      // Crear valor basado en el tipo
      switch (mostCommonType) {
        case 'string':
        case 'image':
          hybridStructure[key] = firstElementHasKey ? firstElement[key] : '';
          break;
        case 'number':
          hybridStructure[key] = firstElementHasKey ? firstElement[key] : 0;
          break;
        case 'boolean':
          hybridStructure[key] = firstElementHasKey ? firstElement[key] : false;
          break;
        case 'array':
          // Para arrays, crear estructura híbrida recursivamente si contiene objetos
          const arrayValues = values.filter(v => Array.isArray(v));
          if (arrayValues.length > 0 && arrayValues[0].length > 0 && typeof arrayValues[0][0] === 'object') {
            // Usar el array del primer elemento si existe, sino el primero disponible
            const arrayToAnalyze = firstElementHasKey && Array.isArray(firstElement[key]) 
              ? firstElement[key] 
              : arrayValues[0];
            hybridStructure[key] = arrayToAnalyze;
          } else {
            hybridStructure[key] = firstElementHasKey ? firstElement[key] : [];
          }
          break;
        case 'object':
          // Para objetos anidados, usar el valor del primer elemento si existe
          if (firstElementHasKey) {
            hybridStructure[key] = firstElement[key];
          } else {
            // Si no existe en el primer elemento, crear estructura híbrida de los objetos disponibles
            const objectValues = values.filter(v => typeof v === 'object' && v !== null && !Array.isArray(v));
            if (objectValues.length > 0) {
              hybridStructure[key] = this.createHybridArrayItemStructure(objectValues);
            } else {
              hybridStructure[key] = {};
            }
          }
          break;
        default:
          hybridStructure[key] = firstElementHasKey ? firstElement[key] : '';
      }
    });
    
    return hybridStructure;
  }

  /**
   * Obtiene el tipo más común de un array de tipos
   */
  private static getMostCommonType(types: string[]): string {
    const typeCount: Record<string, number> = {};
    types.forEach(type => {
      typeCount[type] = (typeCount[type] || 0) + 1;
    });
    
    return Object.entries(typeCount)
      .sort(([,a], [,b]) => b - a)[0][0];
  }

  static updateValue(data: any, path: string, newValue: any): any {
    const keys = path.split('.');
    
    // Si el data original es un array, preservar esa estructura
    if (Array.isArray(data)) {
      const result = [...data];
      let current: any = result;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (key.includes('[') && key.includes(']')) {
          // Manejar arrays
          const arrayKey = key.substring(0, key.indexOf('['));
          const index = parseInt(key.substring(key.indexOf('[') + 1, key.indexOf(']')));
          if (!(current as any)[arrayKey]) (current as any)[arrayKey] = [];
          if (!(current as any)[arrayKey][index]) (current as any)[arrayKey][index] = {};
          current = (current as any)[arrayKey][index];
        } else {
          // Si es un índice numérico directo (para arrays raíz)
          const index = parseInt(key);
          if (!isNaN(index)) {
            if (!current[index]) current[index] = {};
            current = current[index];
          } else {
            if (!(current as any)[key]) (current as any)[key] = {};
            current = (current as any)[key];
          }
        }
      }

      const lastKey = keys[keys.length - 1];
      if (lastKey.includes('[') && lastKey.includes(']')) {
        const arrayKey = lastKey.substring(0, lastKey.indexOf('['));
        const index = parseInt(lastKey.substring(lastKey.indexOf('[') + 1, lastKey.indexOf(']')));
        if (!(current as any)[arrayKey]) (current as any)[arrayKey] = [];
        (current as any)[arrayKey][index] = newValue;
      } else {
        const index = parseInt(lastKey);
        if (!isNaN(index)) {
          current[index] = newValue;
        } else {
          (current as any)[lastKey] = newValue;
        }
      }

      return result;
    } else {
      // Lógica original para objetos
      const result = { ...data };
      let current: any = result;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (key.includes('[') && key.includes(']')) {
          // Manejar arrays
          const arrayKey = key.substring(0, key.indexOf('['));
          const index = parseInt(key.substring(key.indexOf('[') + 1, key.indexOf(']')));
          if (!current[arrayKey]) current[arrayKey] = [];
          if (!current[arrayKey][index]) current[arrayKey][index] = {};
          current = current[arrayKey][index];
        } else {
          if (!current[key]) current[key] = {};
          current = current[key];
        }
      }

      const lastKey = keys[keys.length - 1];
      if (lastKey.includes('[') && lastKey.includes(']')) {
        const arrayKey = lastKey.substring(0, lastKey.indexOf('['));
        const index = parseInt(lastKey.substring(lastKey.indexOf('[') + 1, lastKey.indexOf(']')));
        if (!current[arrayKey]) current[arrayKey] = [];
        current[arrayKey][index] = newValue;
      } else {
        current[lastKey] = newValue;
      }

      return result;
    }
  }

  static addArrayItem(data: any, path: string, newItem: any): any {
    const keys = path.split('.');
    
    // Si el data original es un array, preservar esa estructura
    if (Array.isArray(data)) {
      const result = [...data];
      let current: any = result;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const index = parseInt(key);
        if (!isNaN(index)) {
          if (!current[index]) current[index] = {};
          current = current[index];
        } else {
          if (!(current as any)[key]) (current as any)[key] = {};
          current = (current as any)[key];
        }
      }

      const lastKey = keys[keys.length - 1];
      const index = parseInt(lastKey);
      if (!isNaN(index)) {
        if (!current[index]) current[index] = [];
        current[index] = [...current[index], newItem];
      } else {
        if (!(current as any)[lastKey]) (current as any)[lastKey] = [];
        (current as any)[lastKey] = [...(current as any)[lastKey], newItem];
      }

      return result;
    } else {
      // Lógica original para objetos
      const result = { ...data };
      let current: any = result;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key]) current[key] = {};
        current = current[key];
      }

      const lastKey = keys[keys.length - 1];
      if (!current[lastKey]) current[lastKey] = [];
      current[lastKey] = [...current[lastKey], newItem];

      return result;
    }
  }

  static removeArrayItem(data: any, path: string, index: number): any {
    const keys = path.split('.');
    
    // Si el data original es un array, preservar esa estructura
    if (Array.isArray(data)) {
      const result = [...data];
      let current: any = result;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const keyIndex = parseInt(key);
        if (!isNaN(keyIndex)) {
          current = current[keyIndex];
        } else {
          current = (current as any)[key];
        }
      }

      const lastKey = keys[keys.length - 1];
      const keyIndex = parseInt(lastKey);
      if (!isNaN(keyIndex)) {
        if (current[keyIndex] && Array.isArray(current[keyIndex])) {
          current[keyIndex] = current[keyIndex].filter((_: any, i: number) => i !== index);
        }
      } else {
        if ((current as any)[lastKey] && Array.isArray((current as any)[lastKey])) {
          (current as any)[lastKey] = (current as any)[lastKey].filter((_: any, i: number) => i !== index);
        }
      }

      return result;
    } else {
      // Lógica original para objetos
      const result = { ...data };
      let current: any = result;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        current = current[key];
      }

      const lastKey = keys[keys.length - 1];
      if (current[lastKey] && Array.isArray(current[lastKey])) {
        current[lastKey] = current[lastKey].filter((_: any, i: number) => i !== index);
      }

      return result;
    }
  }
}
